import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { gqlClient } from "@/lib/graphql";
import type { AudioRegenChoice } from "@/hooks/useTranslate";

const QUERY = gql`
  query AudioReview($slug: String!) {
    book(slug: $slug) {
      id
      audioReviewCount
      audioReviewSegments {
        id
        chapterId
        chapterTitle
        externalId
        targetText
      }
    }
  }
`;

const SET_POLICY = gql`
  mutation SetAudioRegenPolicy($segmentId: ID!, $policy: String!) {
    setAudioRegenPolicy(input: { segmentId: $segmentId, policy: $policy }) {
      segment { id needsAudioDecision }
      errors
    }
  }
`;

export interface AudioReviewSegment {
  id: string;
  chapterId: string | null;
  chapterTitle: string | null;
  externalId: string;
  targetText: string | null;
}

export interface AudioReviewData {
  id: string;
  audioReviewCount: number;
  audioReviewSegments: AudioReviewSegment[];
}

export function useAudioReview(slug: string, enabled = true) {
  return useQuery({
    queryKey: ["audio-review", slug],
    enabled,
    queryFn: async () =>
      (await gqlClient.request<{ book: AudioReviewData }>(QUERY, { slug })).book,
  });
}

// Resolves one review item (regenerate now / on next play / keep). Refreshes the
// review queue, the header count, and the open chapter (audio may change).
export function useResolveAudioReview(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ segmentId, policy }: { segmentId: string; policy: AudioRegenChoice }) => {
      const res = await gqlClient.request<{
        setAudioRegenPolicy: { segment: { id: string } | null; errors: string[] };
      }>(SET_POLICY, { segmentId, policy });
      if (res.setAudioRegenPolicy.errors.length)
        throw new Error(res.setAudioRegenPolicy.errors.join(", "));
      return res.setAudioRegenPolicy.segment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["audio-review", slug] });
      qc.invalidateQueries({ queryKey: ["sync-status", slug] });
      qc.invalidateQueries({ queryKey: ["chapter"] });
    },
  });
}
